import React, { useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";

const fmtDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-BO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};
const tableHeadBg = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.grey[100];

const prettyMeta = (meta) => {
  if (!meta) return "-";
  try {
    const parsed = typeof meta === "string" ? JSON.parse(meta) : meta;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return String(meta);
  }
};

const getHistorialFecha = (row) =>
  row?.fecha_registro || row?.created_at || row?.createdAt || row?.fecha || row?.fecha_creacion || row?.updated_at;

const getUnidadPosition = (u) => {
  const pos = u?.pos || u?.lmsg?.pos || {};
  const lat = Number(pos.y ?? pos.lat);
  const lng = Number(pos.x ?? pos.lon ?? pos.lng);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
};

const getUnidadLastTime = (u) => u?.pos?.t || u?.lmsg?.t || u?.lmsg?.pos?.t;

export default function UsuarioIntegracionDetalleDialog({
  open,
  loading = false,
  data = null,
  onClose,
}) {
  const [tab, setTab] = useState(0);
  const usuario = data?.usuario || {};
  const local = data?.local || {};
  const historial = Array.isArray(data?.historial) ? data.historial : [];
  const grupos = Array.isArray(data?.grupos) ? data.grupos : [];
  const directasSinGrupo = Array.isArray(data?.vehiculos_directos_sin_grupo)
    ? data.vehiculos_directos_sin_grupo
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
        Detalle de usuario
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {data?.provider || "-"} {usuario?.id ? `- ID ${usuario.id}` : ""}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1 }}>
        {loading ? (
          <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ display: "grid", gap: 1 }}>
            <Tabs
              value={tab}
              onChange={(_, value) => setTab(value)}
              sx={{
                minHeight: 34,
                borderBottom: "1px solid #e5e7eb",
                "& .MuiTab-root": { minHeight: 34, py: 0.5, fontSize: "0.78rem", fontWeight: 900, textTransform: "none" },
              }}
            >
              <Tab label="Detalle y grupos" />
              <Tab label={`Historial (${historial.length})`} />
            </Tabs>

            {tab === 0 && (
              <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 0.75,
                "& .label": { color: "text.secondary", fontSize: "0.7rem" },
                "& .value": { fontSize: "0.82rem", fontWeight: 800 },
              }}
            >
              <Box><Typography className="label">Usuario</Typography><Typography className="value">{usuario?.nm || usuario?.name || "-"}</Typography></Box>
              <Box><Typography className="label">Cliente</Typography><Typography className="value">{local?.razon_social || "-"}</Typography></Box>
              <Box><Typography className="label">Servicio</Typography><Typography className="value">{local?.cod_servicio_cliente || "-"} {local?.descripcion ? `- ${local.descripcion}` : ""}</Typography></Box>
              <Box><Typography className="label">Grupo</Typography><Typography className="value">{local?.grupo || "-"}</Typography></Box>
              <Box><Typography className="label">Estado</Typography><Chip size="small" label={local?.estado || "-"} color={String(local?.estado).toLowerCase() === "activo" ? "success" : "default"} /></Box>
              <Box><Typography className="label">Ultima sincronizacion</Typography><Typography className="value">{fmtDate(local?.last_sync)}</Typography></Box>
            </Box>

            <Typography variant="subtitle2" fontWeight={900} sx={{ mt: 0.5 }}>
              Grupos y unidades
            </Typography>

            <Table size="small" sx={{ "& .MuiTableCell-root": { fontSize: "0.74rem", py: 0.55, px: 0.7 } }}>
              <TableHead>
                <TableRow sx={{ "& th": { bgcolor: tableHeadBg, fontWeight: 900 } }}>
                  <TableCell>Grupo</TableCell>
                  <TableCell align="center">Unidades</TableCell>
                  <TableCell>Detalle de unidades</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grupos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ color: "text.secondary" }}>
                      Sin grupos vinculados.
                    </TableCell>
                  </TableRow>
                ) : (
                  grupos.map((grupo) => {
                    const unidades = Array.isArray(grupo.unidades_detalle) ? grupo.unidades_detalle : [];
                    return (
                      <TableRow key={grupo.id || grupo.nm}>
                        <TableCell sx={{ fontWeight: 900 }}>{grupo.nm || grupo.name || grupo.id || "-"}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={unidades.length} sx={{ height: 20, fontSize: "0.68rem", fontWeight: 900 }} />
                        </TableCell>
                        <TableCell>
                          {unidades.length ? (
                            <Box sx={{ display: "grid", gap: 0.4 }}>
                              {unidades.map((unidad) => {
                                const pos = getUnidadPosition(unidad);
                                return (
                                  <Box key={unidad.id || unidad.uid || unidad.nm} sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                                    <Typography sx={{ fontSize: "0.74rem", fontWeight: 900 }}>{unidad.nm || "-"}</Typography>
                                    <Chip size="small" label={`UID: ${unidad.uid || "-"}`} sx={{ height: 18, fontSize: "0.64rem" }} />
                                    <Chip size="small" label={fmtDate(getUnidadLastTime(unidad))} sx={{ height: 18, fontSize: "0.64rem" }} />
                                    {pos && (
                                      <Chip size="small" label={`${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`} sx={{ height: 18, fontSize: "0.64rem" }} />
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: "0.74rem", color: "text.secondary" }}>Sin unidades en el grupo.</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            <Typography variant="subtitle2" fontWeight={900} sx={{ mt: 0.5 }}>
              Unidades directas sin grupo
            </Typography>

            <Table size="small" sx={{ "& .MuiTableCell-root": { fontSize: "0.74rem", py: 0.55, px: 0.7 } }}>
              <TableHead>
                <TableRow sx={{ "& th": { bgcolor: tableHeadBg, fontWeight: 900 } }}>
                  <TableCell>Unidad</TableCell>
                  <TableCell>UID</TableCell>
                  <TableCell>Ultimo reporte</TableCell>
                  <TableCell>Coordenadas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {directasSinGrupo.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: "text.secondary" }}>
                      Sin unidades directas fuera de grupos.
                    </TableCell>
                  </TableRow>
                ) : (
                  directasSinGrupo.map((unidad) => {
                    const pos = getUnidadPosition(unidad);
                    return (
                      <TableRow key={unidad.id || unidad.uid || unidad.nm}>
                        <TableCell sx={{ fontWeight: 900 }}>{unidad.nm || "-"}</TableCell>
                        <TableCell>{unidad.uid || "-"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(getUnidadLastTime(unidad))}</TableCell>
                        <TableCell>
                          {pos ? `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}` : "-"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
              </>
            )}

            {tab === 1 && (
            <Table size="small" sx={{ "& .MuiTableCell-root": { fontSize: "0.76rem", py: 0.65, px: 0.75 } }}>
              <TableHead>
                <TableRow sx={{ "& th": { bgcolor: tableHeadBg, fontWeight: 900 } }}>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Accion</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Servicio</TableCell>
                  <TableCell>Detalle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historial.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>
                      Sin historial registrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  historial.map((h) => (
                    <TableRow key={h.id || h.cod || `${h.accion}-${getHistorialFecha(h)}`}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(getHistorialFecha(h))}</TableCell>
                      <TableCell>{h.accion || "-"}</TableCell>
                      <TableCell>{h.actor || "-"}</TableCell>
                      <TableCell>{h.cod_servicio_cliente || "-"}</TableCell>
                      <TableCell>
                        <Box component="pre" sx={{ m: 0, whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: "0.68rem" }}>
                          {prettyMeta(h.meta)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ py: 0.75 }}>
        <Button size="small" onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
